"use client";

import * as React from "react";
import { useNavigate } from "react-router-dom";

import {
  Heading,
  Flex,
  Link,
  Collection,
  Card,
  Badge,
  Grid,
} from "@aws-amplify/ui-react";

import { generateClient } from "aws-amplify/api";
import { Helmet } from "react-helmet";

import { Schema } from "../../../amplify/data/resource";
import { hasAccess } from "../../acl/groups";
import { I18n } from "aws-amplify/utils";
import { FaArrowLeft, FaPencil, FaPlus } from "react-icons/fa6";
import LoadingDialog from "../../components/LoadingDialog";

const client = generateClient<Schema>();

type Pattern = Schema["Pattern"]["type"];
type DanceStyle = Schema["DanceStyle"]["type"];

interface PatternsProps {
  groups: string[];
}

const Patterns: React.FC<PatternsProps> = ({ groups }) => {
  const navigate = useNavigate();
  const [patternRecords, setPatterns] = React.useState<Pattern[]>([]);

  const [danceStylesMap, setDanceStylesMap] = React.useState<
    Map<string, DanceStyle>
  >(new Map<string, DanceStyle>());
  const [patternDanceStylesMap, setPatternDanceStylesMap] = React.useState<
    Map<string, Pattern[]>
  >(new Map<string, Pattern[]>());

  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  React.useEffect(() => {
    markLoading("danceStyles", true);
    const danceStylesSub = client.models.DanceStyle.observeQuery().subscribe({
      next: ({ items }) => {
        const danceStylesMap = new Map<string, DanceStyle>();
        items.forEach((danceStyle: DanceStyle) => {
          danceStylesMap.set(danceStyle.id, danceStyle);
        });
        setDanceStylesMap(danceStylesMap);
        markLoading("danceStyles", false);
      },
    });

    return () => {
      danceStylesSub.unsubscribe();
    };
  }, []);

  React.useEffect(() => {
    const query = hasAccess(groups, "patterns", "update")
      ? {}
      : {
          filter: {
            status: { eq: true },
          },
        };
    markLoading("patterns", true);
    const patternsSub = client.models.Pattern.observeQuery(query).subscribe({
      next: ({ items }) => {
        const patternDanceStylesMap = new Map<string, Pattern[]>();
        items.forEach((pattern: Pattern) => {
          const danceStyle = pattern.danceStylePatternsId ?? "";
          if (patternDanceStylesMap.has(danceStyle)) {
            patternDanceStylesMap.get(danceStyle)?.push(pattern);
          } else {
            patternDanceStylesMap.set(danceStyle, [pattern]);
          }
        });
        setPatternDanceStylesMap(patternDanceStylesMap);
        markLoading("patterns", false);
      },
    });
    return () => {
      patternsSub.unsubscribe();
    };
  }, [danceStylesMap, groups]);

  React.useEffect(() => {
    patternDanceStylesMap.forEach((patterns) => {
      patterns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
    const orderedRecords: Pattern[] = [];
    patternDanceStylesMap.forEach((patterns) => {
      orderedRecords.push(...patterns);
    });
    setPatterns(orderedRecords);
  }, [patternDanceStylesMap]);

  return (
    <>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex gap="1rem" direction="column">
          <Helmet>
            <title>{I18n.get("Patterns")}</title>
          </Helmet>
          <Flex marginTop={"1rem"}>
            <Heading level={1}>{I18n.get("Patterns")}</Heading>
          </Flex>
          <Flex>
            <Link onClick={() => navigate(-1)}>
              <FaArrowLeft aria-label={I18n.get("Back")} />
            </Link>
            {hasAccess(groups, "patterns", "create") && (
              <Link href="/patterns/create">
                <FaPlus aria-label={I18n.get("Create")} />
              </Link>
            )}
          </Flex>
          <Grid>
            <Collection
              items={patternRecords}
              type="list"
              direction="row"
              wrap="wrap"
              alignContent="flex-start"
              searchPlaceholder={I18n.get("Type to search...")}
              isPaginated
              isSearchable
            >
              {(pattern: Pattern) => (
                <Card
                  key={pattern.id}
                  title={pattern.name}
                  variation="outlined"
                  borderRadius="medium"
                  alignSelf="flex-start"
                  width={{ base: "100%", medium: "48.8%", large: "18.9%" }}
                >
                  <Flex
                    direction="column"
                    marginBottom="1rem"
                    minHeight={{ base: "2lh", large: "3lh" }}
                    textAlign="center"
                  >
                    <Heading>
                      <Link href={`/patterns/${pattern.id}/view`}>
                        {pattern.name}
                      </Link>
                      {hasAccess(groups, "patterns", "update") && (
                        <>
                          &nbsp;{" "}
                          <Link href={`/patterns/${pattern.id}/update`}>
                            <FaPencil aria-label={I18n.get("Edit")} />
                          </Link>
                        </>
                      )}
                    </Heading>
                  </Flex>
                  <Flex
                    direction="row"
                    wrap="wrap"
                    justifyContent="center"
                    marginBottom="1rem"
                  >
                    <Badge
                      backgroundColor={
                        danceStylesMap.get(pattern.danceStylePatternsId ?? "")
                          ?.discipline === "Latin"
                          ? "purple.40"
                          : "yellow.40"
                      }
                    >
                      <Link
                        href={`/dance-styles/${pattern.danceStylePatternsId}/view`}
                        color={"black"}
                      >
                        {
                          danceStylesMap.get(pattern.danceStylePatternsId ?? "")
                            ?.name
                        }
                      </Link>
                    </Badge>
                    <Badge backgroundColor={"blue.40"}>
                      #{pattern.order && pattern.order / 10}
                    </Badge>
                    <Badge
                      backgroundColor={pattern.status ? "green.40" : "gray.40"}
                    >
                      {pattern.status
                        ? I18n.get("Active")
                        : I18n.get("Disabled")}
                    </Badge>
                  </Flex>
                </Card>
              )}
            </Collection>
          </Grid>
        </Flex>
      )}
    </>
  );
};

export default Patterns;
